<template>
    <nav 
        :class="context.isDarkMode ? 'dark-mode bg-secondary-color text-white' : 'light-mode bg-white text-secondary-color'"
        class="p-4 fixed w-full top-0 z-10 shadow" 
        style="z-index: 1100;"
    >
        <div class="container mx-auto flex justify-between items-center">
            <RouterLink :to="'/?section='" class="text-xl font-bold flex cursor-pointer">
                <img src="img/icon.webp" width="30" height="30" class="my-auto rounded-full mr-2" alt="logo image">
                <span class="my-auto">
                    Cervo Digital
                </span>
            </RouterLink>
            <ul id="menu" :class="context.isDarkMode ? 'dark-mode' : 'light-mode'" class="hidden md:flex space-x-6">
                <li v-for="(item, index) of avaliableItems" :key="index" class="list-item-effect">
                    <RouterLink 
                        :to="item.link"
                        :class="context.isDarkMode ? 'hover:text-primary-dark-color' : 'hover:text-primary-light-color'"
                        class="relative py-2 px-1 transition-all duration-300 ease-in-out"
                    >
                        {{ translationHandler.translate(item.title, 'ucfirst') }}
                    </RouterLink>
                </li>
                <li class="text-xl my-auto list-item-effect">
                    <i 
                        class="pi pi-language cursor-pointer" 
                        :class="context.isDarkMode ? 'hover:text-primary-dark-color' : 'hover:text-primary-light-color'"
                        @click="showLanguagesModal = !showLanguagesModal"
                    ></i>
                </li>
                <li class="text-xl my-auto list-item-effect">
                    <i 
                        class="pi cursor-pointer" 
                        :class="context.isDarkMode ? 'pi-sun hover:text-primary-dark-color' : 'pi-moon hover:text-primary-light-color'" 
                        @click="changeMode()"
                    ></i>
                </li>
            </ul>
            <img :src="!showMobileMenu ? require(`@/assets/images/menu-icon-${context.getColorMode}.svg`) : require(`@/assets/images/close-icon-${context.getColorMode}.svg`)"
                width="30" height="30" class="md:hidden cursor-pointer" @click="toggleMobileMenu()" alt="menu icon">
        </div>

        <div v-if="showMobileMenu" class="flex flex-col" :class="context.isDarkMode ? 'dark-mode bg-secondary-color text-white' : 'light-mode bg-white text-secondary-color'">
            <div class="flex-1">
                <ul class="mt-6 space-y-10 pt-5 ml-5">
                    <li v-for="(item, index) of avaliableItems" :key="index">
                        <RouterLink :to="item.link" class="text-4xl flex justify-between w-full align-middle"
                            @click="toggleMobileMenu(item.id)">
                            <div class="my-auto flex">
                                <p class="text-gray-700 ml-3 my-auto text-lg font-medium">
                                    {{ translationHandler.translate(item.title, 'ucfirst') }}
                                </p>
                            </div>
                        </RouterLink>
                    </li>

                    <li class="flex items-center cursor-pointer transition"
                        @click="showLanguagesModal = !showLanguagesModal">
                        <div class="flex justify-between w-full">
                            <div class="my-auto flex">
                                <p class="text-gray-700 ml-3 my-auto text-lg font-medium">
                                    {{ translationHandler.translate('translation', 'ucfirst') }}
                                </p>
                                <i class="pi pi-language rounded-full p-2 border ml-5"></i>
                            </div>
                        </div>
                    </li>

                    <li class="flex items-center cursor-pointer transition">
                        <div class="my-auto flex ml-3" @click="changeMode()">
                            <p class="text-gray-700 my-auto text-lg font-medium">
                                {{ translationHandler.translate('color mode', 'ucfirst') }}
                            </p>
                            <i 
                                class="pi cursor-pointer p-2 rounded-full border ml-5" 
                                :class="context.isDarkMode ? 'pi-sun hover:text-primary-dark-color' : 'pi-moon hover:text-primary-light-color'" 
                            ></i>
                        </div>
                    </li>

                </ul>
            </div>
        </div>
    </nav>

    <Dialog 
        role="dialog"
        aria-label="Seleção de idioma do site"
        v-model:visible="showLanguagesModal"
        :header="translationHandler.translate('choose your language', 'ucfirst')" 
        :style="{ width: '25rem' }"
        :class="context.isDarkMode ? 'dark-modal' : 'light-modal'"
        :draggable="false"
    >
        <div class="flex flex-col gap-2">
            <button v-for="(lang, index) of avaliableLangs" :key="index"
                class="text-gray-600 font-bold py-2 rounded transition flex justify-center border border-iron hover:bg-iron"
                :class="selectedLang == lang.langIso ? 'hidden' : ''" @click="applyTranslations(lang.langIso)">
                <img :src="getImagePath(lang.flag ? lang.flag : lang.langIso)" :alt="lang.langIso" width="30"
                    height="30">
                <span class="translate-text ml-3">{{ translationHandler.translate(lang.title) }}</span>
            </button>
        </div>
    </Dialog>

    <Dialog v-model:visible="showAutomaticQuoteAdvertisement" :style="{ width: '30rem' }" :modal="true" :closable="true"
        :draggable="false" class="mx-2 md:mx-auto" :class="context.isDarkMode ? 'dark-modal' : 'light-modal'" 
        role="dialog" aria-label="Seleção de acesso ao orçamento automatizado">
        <div class="flex flex-col gap-4 p-4">
            <div class="flex items-center gap-3">
                <div class="rounded-full p-3">
                    <i class="pi pi-wallet text-xl" :class="context.isDarkMode ? 'text-silver' : 'text-void'"></i>
                </div>
                <div>
                    <h2 class="text-lg font-bold">
                        {{ translationHandler.translate('Want to know the price of a website?') }}
                    </h2>
                    <p class="text-sm text-gray-500">
                        {{ translationHandler.translate('Simulate now and find out how much it costs to develop your project online.') }}
                    </p>
                </div>
            </div>

            <div class="rounded-lg p-3 text-sm" :class="context.isDarkMode ? 'bg-dark-silver text-silver' : 'bg-silver text-third-color'">
                {{ translationHandler.translate('Use our interactive tool to get a personalized price estimate in seconds.') }}
            </div>

            <div class="w-full">
                <div class="ml-auto mt-5 max-width-md flex gap-2 content-center primary-button grow-effect rounded-3xl px-4 shadow" @click="requestHandler.redirect('/budget')" >
                    <span class="text-lg lg:text-2xl my-auto text-silver font-semibold mx-auto md:mx-0">
                        {{ translationHandler.translate('order budget', 'capitalize') }}
                    </span>
                    <i class="ml-auto text-silver pi pi-arrow-right border border-second-color rounded-full bg-second-color p-2 my-auto shadow"></i>
                </div>
            </div>
        </div>
    </Dialog>

    <Dialog v-model:visible="showCookieDialog" 
        :modal="false" :closable="false" 
        :draggable="false"
        :style="{ maxWidth: '480px' }" 
        class="fixed z-50 p-0 m-0 bottom-4 left-4 right-4 mx-auto sm:left-4 sm:mx-0 rouned"
        :class="context.isDarkMode ? 'dark-modal' : 'light-modal'"
        role="dialog"
        aria-label="Seleção de aceitação do cookie"
    >
        <div class="p-4 flex flex-col gap-3 text-gray-700">
            <h3 class="text-base font-semibold">
                {{ translationHandler.translate('This website uses cookies') }}
            </h3>
            <p class="text-sm">
                {{ translationHandler.translate('Utilizamos cookies para melhorar sua experiência de navegação, personalizar conteúdo e analisar o tráfego.Ao continuar, você concorda com o uso de cookies.') }}
            </p>
            <div class="flex flex-col md:flex-row justify-end gap-2 md:gap-5 mt-2">
                <div 
                    class="md:mt-5 flex gap-2 content-center primary-button grow-effect rounded-3xl px-4 shadow" 
                    @click="rejectCookies" 
                >
                    <span class="my-auto text-silver mx-auto md:mx-0">
                        {{ translationHandler.translate('Reject', 'capitalize') }}
                    </span>
                    <i class="text-silver pi pi-times border border-second-color rounded-full bg-second-color p-2 my-auto shadow"></i>
                </div>
                <div 
                    class="md:mt-5 flex gap-2 content-center primary-button grow-effect rounded-3xl px-4 shadow" 
                    @click="acceptCookies" 
                >
                    <span class="my-auto text-silver mx-auto md:mx-0">
                        {{ translationHandler.translate('Accept', 'capitalize') }}
                    </span>
                    <i class="text-silver pi pi-check border border-second-color rounded-full bg-second-color p-2 my-auto shadow"></i>
                </div>
            </div>
        </div>
    </Dialog>

</template>
<script setup>
import { onMounted, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import { useRoute, useRouter } from 'vue-router';
import DomHandler from '@/composables/handlers/DomHandler';
import { useContextStore } from "@/stores/context";
import translationHandler from '@/composables/handlers/TranslationHandler';
import requestHandler from '@/composables/requestHandler';
import cookiesHandler from '@/composables/cookiesHandler';
import { sleep } from '@/composables/sleep';

const context = useContextStore();
const router = useRouter();
const route = useRoute();
const selectedLang = ref('pt');
const avaliableLangs = ref([
    { title: 'english', langIso: 'en' },
    { title: 'portuguese', langIso: 'pt', flag: 'br' },
    { title: 'spanish', langIso: 'es' }
]);
const avaliableItems = ref([
    { link: '/?section=', title: 'home', icon: 'pi-home', id: 'home', to: 'hero-section' },
    { link: '/?section=about-section', title: 'about', icon: 'pi-book', id: 'about', to: 'about-section' },
    { link: '/?section=service-section', title: 'services', icon: 'pi-hammer', id: 'services', to: 'service-section' },
    { link: '/?section=case-section', title: 'clients', icon: 'pi-address-book', id: 'clients', to: 'client-section' },
    { link: '/?section=contact-section', title: 'contact', icon: 'pi-envelope', id: 'contact', to: 'contact-section' }
]);
const showMobileMenu = ref(false);
const showLanguagesModal = ref(false);
const fullRoute = ref('');
const ignoreUrlChange = ref(true);
const htmlElement = ref();
const selectedMobileMenu = ref('home');
const showAutomaticQuoteAdvertisement = ref(false);
const showCookieDialog = ref(false);

onMounted(() => {
    if (!context.isLoadingApplication) {
        triggerLoadedMethods();
    }
});

const watcher = watch(
  () => context.isLoadingApplication,
  () => {
    if (alreadyTriggeredLoadedMethods.value) {
        watcher();
    }

    if (!context.isLoadingApplication) {
        triggerLoadedMethods();
    }
  }
);

const alreadyTriggeredLoadedMethods = ref(false);
function triggerLoadedMethods() {
    if (alreadyTriggeredLoadedMethods.value == true) {
        return;
    }
    alreadyTriggeredLoadedMethods.value = true;

    fullRoute.value = route.path;
    scrollToSection();

    showCookieAcceptance();
    checkPreferendLang();
    showBudgetAdvertisement();

    checkParameters();
    DomHandler.setHtmlStyle();
}

const advertisementTimer = 8000;
async function showBudgetAdvertisement() {
    const alreadySaw = cookiesHandler.getEncryptedCookie('seen_budget_advertisement');
    
    if ([false, null, undefined, ''].includes(alreadySaw)) {
    
        await sleep(advertisementTimer);
        
        if (route.path != '/budget') {
            showAutomaticQuoteAdvertisement.value = true;
        }

        cookiesHandler.setEncryptedCookie('seen_budget_advertisement', true);
    }
}

const validLanguages = [
    'en',
    'pt',
    'es'
];
function checkParameters() {
    const parameters = route.query;

    let parametersGathered = {
        lang: 'pt'
    };


    const urlParam = { ...route.query };

    for (let param of Object.keys(parameters)) {
        switch (param) {
            case 'lang':
                parametersGathered.lang = parameters[param];
                
                if (validLanguages.includes(parametersGathered.lang)) {
                    applyTranslations(parametersGathered.lang);
                }
            break;
        }

        delete urlParam[param]
    }

    router.replace({ query: urlParam });
}

async function checkPreferendLang() {
    await sleep(500);
    const lang = cookiesHandler.getEncryptedCookie('preferedLang');
    if (!['', null, false, undefined].includes(lang) && context.getAvaliableLangs.includes(lang)) {
        applyTranslations(lang);
    }
}

function applyTranslations(langIso = 'en') {
    selectedLang.value = langIso;
    context.setLang(langIso);
    showLanguagesModal.value = false;
    cookiesHandler.setEncryptedCookie('preferedLang', langIso);
}

function getImagePath(langIso = 'en') {
    return `flags/${langIso}.svg`;
}

watch(
    () => route.query.section,
    () => {
        scrollToSection(true);
    }
)

const elFindingTries = ref(0);
async function scrollToSection() {
    const sectionSelected = route.query.section || null;

    if (ignoreUrlChange.value === true && [null,'',undefined].includes(sectionSelected)) {
        ignoreUrlChange.value = false;
        return;
    }

    if (!sectionSelected) {
        DomHandler.scrollTop();
    } else {
        let el = document.getElementById(sectionSelected);

        while (el == null) {
            await sleep(250);
            el = document.getElementById(sectionSelected);
            elFindingTries.value++;

            if (elFindingTries.value >= 15) {
                break;
            }
        }

        if (el) {
            const elementPosition = el.offsetTop;
            const offset = window.innerHeight * 0.15;
            const offsetPosition = elementPosition - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            elFindingTries.value = 0;
        }
    }

    cleanSectionParameter()
}

function cleanSectionParameter() {
    ignoreUrlChange.value = true;
    const newQuery = { ...route.query };
    delete newQuery.section;
    router.replace({ query: newQuery });
}

function toggleMobileMenu(id = null) {
    showMobileMenu.value = !showMobileMenu.value;
    if (id != null) {
        selectedMobileMenu.value = id;
    }
    toggleScreenScroll();
}

function toggleScreenScroll() {
    if (!htmlElement.value) {
        htmlElement.value = document.querySelector('html');
    }
    let valueToSet = htmlElement.value.style.overflowY == 'hidden' ? '' : 'hidden';
    htmlElement.value.style.overflowY = showMobileMenu.value ? valueToSet : '';
}

async function showCookieAcceptance() {
    await sleep(1500);
    if (!cookiesHandler.hasCookie('accepted_cookies')) {
        showCookieDialog.value = true;
    }
}

function acceptCookies() {
    cookiesHandler.saveCookieAcceptance(true);
    showCookieDialog.value = false;
}

function rejectCookies() {
    cookiesHandler.saveCookieAcceptance(false);
    showCookieDialog.value = false;
}

function changeMode() {
    context.setDarkMode(!context.isDarkMode);

    DomHandler.setHtmlStyle();
}
</script>